/**
 * Intraday charts — seconds, minutes and hours (`docs/plans/intraday.md`).
 *
 * Three decisions a chart makes differently once its bars are inside a day,
 * pure and tested here because `ChartGroupView` is out of jsdom's reach:
 *
 * - **How the time axis reads**: the exchange's clock, not a date.
 * - **How many bars one request may bring**: `QV_INTRADAY_MAX_BARS`, not 5000.
 * - **When to fetch older bars**: before the reader pans into the first one.
 */

import type { Frequency } from "@qufolio/model";
import type { TimeAxisLabels } from "@qufolio/chart-engine";
import { splitInterval } from "./interval-mru";

/**
 * The most intraday bars one request returns — `QV_INTRADAY_MAX_BARS` in
 * qv-data. A copy, for `MAX_LOADED_BARS`' reason: the chart has to know where
 * the ceiling is before it asks, or it asks forever. The server still clamps.
 */
export const MAX_INTRADAY_BARS = 20000;

/**
 * Bars an intraday chart draws when it opens — `QV_INTRADAY_FIRST_BARS`. The
 * server fetches a warm-up of the same size in front and does not send it.
 *
 * **The chart's saved `barsRange` is not used for intraday** (owner,
 * 2026-09-28): pans used to save their doubled load into the workspace, so a
 * chart panned once reopened at 12,000 bars and took 27 s. The loaded count is
 * now the view's own and starts here every time.
 */
export const INTRADAY_FIRST_BARS = 1000;

/** Whether an interval is inside a day — seconds, minutes or hours. */
export function isIntraday(frequency: Frequency | string | undefined): boolean {
  if (!frequency) return false;
  const { unit } = splitInterval(frequency as Frequency);
  return unit === "s" || unit === "mi" || unit === "h";
}

/**
 * The exchange a symbol trades on, as a time zone.
 *
 * By suffix, the way the rest of the app tells markets apart (D40): `.NS` and
 * `.BO` are India, everything else is the US. Only the US is served intraday in
 * phase 1, but the label must not be wrong the day India is.
 */
export function exchangeTimeZone(symbol: string): string {
  const upper = symbol.toUpperCase();
  return upper.endsWith(".NS") || upper.endsWith(".BO") ? "Asia/Kolkata" : "America/New_York";
}

/** How the time axis should write this chart's bars. */
export function timeLabelsFor(symbol: string, frequency: Frequency | string | undefined): TimeAxisLabels {
  if (!isIntraday(frequency)) return { kind: "date" };
  return {
    kind: "time",
    timeZone: exchangeTimeZone(symbol),
    seconds: splitInterval(frequency as Frequency).unit === "s",
  };
}

/** What to do about the window having moved: nothing, load more, or say it is the end. */
export type PanLoad = { barsRange: number } | { atLimit: true } | undefined;

/**
 * Whether a pan has come close enough to the first loaded bar to fetch more.
 *
 * **Within one screen of it**, so the older bars arrive before the reader runs
 * out — the request takes about a second, and a chart that stops at an edge and
 * then jumps is worse than one that is simply further back than you looked.
 * **Doubling** the load, so a long pan costs a handful of requests rather than
 * one per screen, up to `maxBars`; past it, `atLimit`, once, so the chart can
 * say where history ends instead of asking again.
 *
 * `complete` is the server saying there is nothing older within the depth
 * limit (`qv.complete`), and it ends the question.
 */
export function planPanLoad(opts: {
  /** Loaded bar times, ascending, unix seconds. */
  x: readonly number[];
  /** The left edge of the window, unix seconds. */
  windowMin: number;
  /** Bars on screen now. */
  visibleBars: number;
  /** The chart's `barsRange` as stored. */
  barsRange: number | undefined;
  complete: boolean;
  maxBars?: number;
}): PanLoad {
  const { x } = opts;
  if (opts.complete || x.length === 0) return undefined;
  const maxBars = opts.maxBars ?? MAX_INTRADAY_BARS;
  // Bars between the first loaded one and the window's left edge.
  let lo = 0;
  let hi = x.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (x[mid]! < opts.windowMin) lo = mid + 1;
    else hi = mid;
  }
  if (lo > Math.max(1, opts.visibleBars)) return undefined;
  const loaded = Math.max(opts.barsRange ?? 0, x.length);
  if (loaded >= maxBars) return { atLimit: true };
  return { barsRange: Math.min(maxBars, loaded * 2) };
}

// ── Showing that a chart is live (owner-approved mockup, 2026-09-29) ─────────

/** What the dot top-right says. `null`: say nothing — not an intraday chart. */
export type LiveState = "live" | "delayed" | "closed";

/**
 * How long without a frame before "live" becomes "delayed". The server
 * refreshes an intraday chart every 5 s; three missed is not a quiet minute.
 */
export const LIVE_STALE_MS = 15_000;

/**
 * The dot's state from the server's latest facts (`qv.today`, `qv.phase`).
 *
 * - **closed** — no bars are being made (night, weekend, holiday).
 * - **live** — today comes from qf-intra, the live feed, and a frame arrived
 *   within {@link LIVE_STALE_MS}.
 * - **delayed** — the market is open but today comes from the REST fallback,
 *   or the frames stopped: the chart still updates, just not from the feed.
 */
export function liveState(
  info: { todaySource?: "intra" | "rest"; phase?: "live" | "closed"; receivedAt: number } | null,
  nowMs: number,
): LiveState | null {
  if (!info || (info.todaySource === undefined && info.phase === undefined)) return null;
  if (info.phase === "closed") return "closed";
  if (info.todaySource === "intra" && nowMs - info.receivedAt < LIVE_STALE_MS) return "live";
  return "delayed";
}

/** An intraday interval's length in seconds, or `undefined` for daily and coarser. */
export function periodSeconds(frequency: Frequency | string | undefined): number | undefined {
  if (!isIntraday(frequency)) return undefined;
  const { count, unit } = splitInterval(frequency as Frequency);
  return count * (unit === "s" ? 1 : unit === "mi" ? 60 : 3600);
}

/**
 * Time left in the forming bar, as the price label shows it: `mm:ss`, or
 * `h:mm:ss` from an hour. `00:00` once the bar's time is up and the next has
 * not arrived yet — never a negative count.
 *
 * The last bar of a session can be short (`15:30` on an hourly chart ends at
 * 16:00, not 16:30); the countdown runs to the bar's nominal end, which is
 * what the chart's own bucketing promises.
 */
export function countdown(barStartSec: number, periodSec: number, nowSec: number): string {
  const left = Math.max(0, Math.floor(barStartSec + periodSec - nowSec));
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  const two = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${two(m)}:${two(s)}`;
}

/**
 * Whether the last bar ended more than `graceSec` ago — no bar is forming, so
 * there is no countdown and no band. After the close on a regular-hours chart
 * that is all evening; mid-session it is only a push that is late.
 */
export function barOver(barStartSec: number, periodSec: number, nowSec: number, graceSec = 60): boolean {
  return nowSec > barStartSec + periodSec + graceSec;
}

/** The band behind the forming bar: centred on its candle, one bar wide. */
export function formingBand(barStartSec: number, periodSec: number): { start: number; end: number } {
  return { start: barStartSec - periodSec / 2, end: barStartSec + periodSec / 2 };
}
